import axiosConfig from "./axiosConfig";

export type SurveySubmissionStatus = "in_progress" | "submitted";
export type SurveySubmissionKind = "on_site_survey" | "cost_sheet";

export interface SurveySubmissionSummary {
  id: string;
  kind: SurveySubmissionKind;
  is_submitted: boolean;
  status: SurveySubmissionStatus;
  pdf_s3_key: string | null;
  site_file_guid: string | null;
  // Latest cost-sheet version number (cost_sheet submissions only).
  latest_version?: number | null;
  created_at: string;
  updated_at: string;
}

export interface SurveyProjectSummary {
  id: string;
  name: string;
  site_id: string | null;
  created_at: string;
  updated_at: string;
  submissions: SurveySubmissionSummary[];
}

export type SurveyLanguage = "en" | "nl";

export interface SurveyEndpointRecord {
  id: string;
  name: string;
  shortcode: string;
  org_id: string | null;
  org_name: string | null;
  default_language?: SurveyLanguage | null;
  created_at: string;
  // Present on /survey/endpoints and /organisation/{org_id}/surveys.
  projects?: SurveyProjectSummary[];
}

export interface SurveyEndpointsResponse {
  endpoints: SurveyEndpointRecord[];
}

export interface CreateSurveyEndpointResponse {
  endpoint: SurveyEndpointRecord;
}

export interface UpdateProjectSiteResponse {
  project: {
    id: string;
    survey_endpoint_id: string;
    name: string;
    site_id: string | null;
    updated_at: string;
  };
}

export interface UpdateSurveyEndpointOrgResponse {
  endpoint: SurveyEndpointRecord;
}

export interface UpdateSurveyEndpointLanguageResponse {
  endpoint: SurveyEndpointRecord;
}

export interface CostSheetVersionSummary {
  id: string;
  version_number: number;
  total: number;
  total_per_kwp: number | null;
  currency: string;
  submitted_at: string;
}

export interface ListCostSheetVersionsResponse {
  versions: CostSheetVersionSummary[];
}

export type CostSheetImportSeverity = "error" | "notice";

export interface CostSheetImportIssue {
  severity: CostSheetImportSeverity;
  message: string;
  section: string | null;
  row: string | null;
  cell: string | null;
}

export interface CostSheetImportReport {
  ok: boolean;
  issues: CostSheetImportIssue[];
  rowsImported: number;
  customRowsAdded: number;
  sectionsRecognised: number;
  sectionsRoutedToOther: number;
  linesBelowTotalIgnored: number;
  systemSizeKwp: number | null;
}

export interface ImportCostSheetResponse {
  ok: boolean;
  report: CostSheetImportReport;
  version?: CostSheetVersionSummary;
}

export interface CostSheetImportUrlResponse {
  url: string;
  s3Key: string;
}

export const getSurveyEndpoints = async (
  authToken: string
): Promise<SurveyEndpointsResponse> => {
  const { data } = await axiosConfig.get("/survey/endpoints", {
    headers: { Authorization: `Bearer ${authToken}` },
  });
  return data;
};

export const createSurveyEndpoint = async (
  authToken: string,
  name: string,
  orgId: string
): Promise<CreateSurveyEndpointResponse> => {
  const { data } = await axiosConfig.post(
    "/survey/endpoints",
    { name, org_id: orgId },
    { headers: { Authorization: `Bearer ${authToken}` } }
  );
  return data;
};

export const deleteSurveyEndpoint = async (
  authToken: string,
  id: string
): Promise<void> => {
  await axiosConfig.delete(`/survey/endpoints/${id}`, {
    headers: { Authorization: `Bearer ${authToken}` },
  });
};

export const updateProjectSite = async (
  authToken: string,
  endpointId: string,
  projectId: string,
  siteId: string | null
): Promise<UpdateProjectSiteResponse> => {
  const { data } = await axiosConfig.put(
    `/survey/${endpointId}/projects/${projectId}/site`,
    { site_id: siteId },
    { headers: { Authorization: `Bearer ${authToken}` } }
  );
  return data;
};

export const updateSurveyEndpointOrg = async (
  authToken: string,
  endpointId: string,
  orgId: string | null
): Promise<UpdateSurveyEndpointOrgResponse> => {
  const { data } = await axiosConfig.put(
    `/survey/endpoints/${endpointId}/org`,
    { org_id: orgId },
    { headers: { Authorization: `Bearer ${authToken}` } }
  );
  return data;
};

export const updateSurveyEndpointLanguage = async (
  authToken: string,
  endpointId: string,
  defaultLanguage: SurveyLanguage | null
): Promise<UpdateSurveyEndpointLanguageResponse> => {
  const { data } = await axiosConfig.put(
    `/survey/endpoints/${endpointId}/language`,
    { default_language: defaultLanguage },
    { headers: { Authorization: `Bearer ${authToken}` } }
  );
  return data;
};

export const adminDeleteSurveyProject = async (
  authToken: string,
  endpointId: string,
  projectId: string
): Promise<void> => {
  await axiosConfig.delete(
    `/survey/${endpointId}/projects/${projectId}/admin`,
    { headers: { Authorization: `Bearer ${authToken}` } }
  );
};

export const getOrgSurveys = async (
  authToken: string,
  orgId: string
): Promise<SurveyEndpointsResponse> => {
  const { data } = await axiosConfig.get(`/organisation/${orgId}/surveys`, {
    headers: { Authorization: `Bearer ${authToken}` },
  });
  return data;
};

export const listCostSheetVersions = async (
  authToken: string,
  endpointId: string,
  submissionId: string
): Promise<ListCostSheetVersionsResponse> => {
  const { data } = await axiosConfig.get(
    `/survey/${endpointId}/submissions/${submissionId}/cost-sheet-versions`,
    { headers: { Authorization: `Bearer ${authToken}` } }
  );
  return data;
};

export const getCostSheetImportUrl = async (
  authToken: string,
  endpointId: string,
  projectId: string,
  fileName: string,
  contentType: string
): Promise<CostSheetImportUrlResponse> => {
  const { data } = await axiosConfig.post(
    `/survey/${endpointId}/projects/${projectId}/cost-sheet/import-url`,
    { fileName, contentType },
    { headers: { Authorization: `Bearer ${authToken}` } }
  );
  return data;
};

export const importCostSheet = async (
  authToken: string,
  endpointId: string,
  projectId: string,
  s3Key: string,
  fileName: string
): Promise<ImportCostSheetResponse> => {
  const { data } = await axiosConfig.post(
    `/survey/${endpointId}/projects/${projectId}/cost-sheet/import`,
    { s3Key, fileName },
    { headers: { Authorization: `Bearer ${authToken}` } }
  );
  return data;
};
